import React, { useState } from 'react';
import { StepperPage } from '../../Stepper';
import Kyc from '../Kyc';
import { FieldError } from '../../VgsInput/field';
import { BeneficialOwnerData } from '../../BeneficialOwners';
import { ShowPayable, StripeMetadataInput, SuccessData } from '../../VaultForm';
import {
  FinancialAccountNode,
  OrgNode,
} from '../../../services/orgs/interfaces';
import { submit } from '../submit';
import { Consumer, StripeEnv } from '../../../util/enums';
import { STATES, TITLES } from '../../../const';
import { DEFAULT_OWNERSHIP_VALUE } from '../../Ownership/const';
import VaultSubmitError from '../../../util/VaultSubmitError';
import logger from '../../../util/logger';

export const useKycStep = ({
  setFieldErrors,
  setErrorMessage,
  org,
  kybAddressRegion,
  kybBusinessStructure,
  kybBusinessType,
  currentUserId,
  form,
  provider,
  financialAccountRoots,
  statementDescriptor,
  metadata,
  stripeEnv,
  payableOrg,
  userKyc,
  fundraiserId,
  fundraiserSlug,
  setSuccessSubmissionStripeAccount,
  consumer,
  onSuccess,
  ...props
}: {
  businessStructure: string;
  form?: VGSForm<{
    vaultCreateKybKycSubmit: VaultFormMutationResponse;
  }>;
  showSavePayableCheckbox: ShowPayable;
  payableOrg?: OrgNode;
  fieldErrors: FieldError[];
  userKyc?: Kyc;
  token: string;
  onRevealFail: () => void;
  onRevealSuccess: () => void;
  setFieldErrors: (fieldErrors: FieldError[]) => void;
  setErrorMessage: (errorMessage?: string) => void;
  org?: OrgNode;
  currentUserId?: string;
  provider: VaultProvider;
  consumer: Consumer;
  kybBusinessStructure: string;
  kybBusinessType: string;
  kybAddressRegion: string;
  financialAccountRoots: FinancialAccountNode[];
  statementDescriptor?: string;
  metadata?: StripeMetadataInput;
  stripeEnv: StripeEnv;
  fundraiserId?: string | null;
  fundraiserSlug?: string | null;
  setSuccessSubmissionStripeAccount?: (stripeConnectAccountId: string) => void;
  onSuccess: (res: SuccessData) => void;
}): StepperPage | null => {
  const [kycIsValid, setKycIsValid] = useState(false);
  const [representativeAddressRegion, setRepresentativeAddressRegion] =
    useState(STATES[0].value);
  const [representativeTitle, setRepresentativeTitle] = useState(
    TITLES[0].value,
  );
  const [representativeIsOwner, setRepresentativeIsOwner] = useState(false);
  const [representativePercentOwnership, setRepresentativePercentOwnership] =
    useState(DEFAULT_OWNERSHIP_VALUE);
  const [saveToPayableOrg, setSaveToPayableOrg] = useState(false);
  const [beneficialOwnersData, setBeneficialOwnersData] = useState<
    BeneficialOwnerData[]
  >([]);
  if (!form) {
    return null;
  }
  const kycStep = {
    component: (
      <Kyc
        {...props}
        onValidChange={setKycIsValid}
        form={form}
        onRepresentativeAddressRegionChange={setRepresentativeAddressRegion}
        onRepresentativeTitleChange={setRepresentativeTitle}
        onRepresentativeIsOwnerChange={setRepresentativeIsOwner}
        onRepresentativePercentOwnershipChange={
          setRepresentativePercentOwnership
        }
        onSaveToPayableOrgChange={setSaveToPayableOrg}
        onBeneficialOwnersChange={setBeneficialOwnersData}
        userKyc={userKyc}
      />
    ),
    isValid: kycIsValid,
    navigation: { showBack: true, showForward: true, showSteps: true },
    nextText: 'Submit for review',
    beforeNext: async (): Promise<void> => {
      setFieldErrors([]);
      setErrorMessage(undefined);
      // catch thrown error
      try {
        const successData = await submit({
          org,
          kybBusinessStructure,
          kybBusinessType,
          kybAddressRegion,
          representativeIsOwner,
          representativePercentOwnership,
          representativeTitle,
          representativeAddressRegion,
          beneficialOwnersData,
          currentUserId,
          form,
          provider,
          saveToPayableOrg,
          financialAccountRoots,
          statementDescriptor,
          metadata,
          payableOrg,
          stripeEnv,
          userKyc,
          fundraiserId,
          fundraiserSlug,
        });
        if (
          consumer === Consumer.INSIGHTS &&
          setSuccessSubmissionStripeAccount !== undefined &&
          successData.kybData.stripeConnectAccountId !== undefined
        ) {
          setSuccessSubmissionStripeAccount(
            successData.kybData.stripeConnectAccountId,
          );
        } else if (consumer === Consumer.INSIGHTS) {
          setErrorMessage('Could not create stripe connect account');
        }
        const onSuccessResponse = {
          kybData: {
            kybId: successData.kybData.kybId,
            org,
            stripeConnectAccountId: successData.kybData.stripeConnectAccountId,
            financialAccountId: successData.kybData.financialAccountId,
            submittedOnYourBehalf: false,
          },
          representative: {
            vaultKycId: successData.representative.kycId,
            userId: successData.representative.userId,
            stripePersonId: successData.representative.stripePersonId,
          },
          beneficialOwners: successData.beneficialOwners.map((bo) => ({
            vaultKycId: bo.kycId,
            userId: bo.userId,
            stripePersonId: bo.userId,
          })),
          payableOrgNode: successData.payableOrgNode
            ? {
                kybData: {
                  kybId: successData.payableOrgNode.kybData.kybId,
                  org: payableOrg,
                  stripeConnectAccountId:
                    successData.payableOrgNode.kybData.stripeConnectAccountId,
                  financialAccountId:
                    successData.payableOrgNode.kybData.financialAccountId,
                  submittedOnYourBehalf: false,
                },
                representative: {
                  vaultKycId: successData.payableOrgNode.representative.kycId,
                  userId: successData.payableOrgNode.representative.userId,
                  stripePersonId:
                    successData.payableOrgNode.representative.userId,
                },
                beneficialOwners: (
                  successData.payableOrgNode.beneficialOwners || []
                ).map((bo) => ({
                  vaultKycId: bo.kycId,
                  userId: bo.userId,
                  stripePersonId: bo.userId,
                })),
              }
            : undefined,
          stripeEnv: StripeEnv[successData.stripeEnv],
        };
        logger(`CreateKybKyc successData ${JSON.stringify(onSuccessResponse)}`);
        onSuccess(onSuccessResponse);
      } catch (e) {
        if (e instanceof VaultSubmitError) {
          if (e.field !== undefined && e.fieldMessage !== undefined) {
            setFieldErrors([
              {
                fieldName: e.field,
                message: e.fieldMessage,
              },
            ]);
          }

          setErrorMessage(e.message);
        } else {
          logger(`Unknown error: ${JSON.stringify(e)}`);
          setErrorMessage(`Unknown error: ${e} ${JSON.stringify(e)}`);
        }
      }
    },
    srName: 'Create KYC',
    shouldRender: true,
  };
  return kycStep;
};
